import { useEffect, useRef } from 'react'

/**
 * Asks `poll` every `intervalMs` while the widget is in view, and at once
 * whenever the customer comes back to this tab, until it resolves true. A
 * rejection is treated as "keep waiting".
 */
export function useSettlePolling(poll: () => Promise<boolean>, intervalMs: number): void {
  // The latest poll, so a re-render does not restart the timer.
  const pollRef = useRef(poll)
  useEffect(() => {
    pollRef.current = poll
  }, [poll])

  useEffect(() => {
    let stopped = false
    let inFlight = false

    const check = async () => {
      if (stopped || inFlight) return
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      inFlight = true
      try {
        if (await pollRef.current()) stopped = true
      } catch {
        // A dropped connection: ask again next time.
      } finally {
        inFlight = false
      }
    }

    const timer = window.setInterval(() => void check(), intervalMs)
    const onReturn = () => void check()
    document.addEventListener('visibilitychange', onReturn)
    window.addEventListener('focus', onReturn)
    void check()

    return () => {
      stopped = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onReturn)
      window.removeEventListener('focus', onReturn)
    }
  }, [intervalMs])
}
